import React, { useCallback, useState, useEffect } from 'react';

import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { PromoMessage as BasePromoMessage } from '~/shared/components/PromoMessage';
import {
  useMembership,
  useMembershipCredits,
} from '~/techstyle-shared/react-accounts';
import {
  mobile,
  Button,
  Heading,
  RadioButton,
  RadioButtonGroup,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import {
  FormattedMessage,
  FormattedDate,
  Currency,
  useIntl,
} from '~/techstyle-shared/react-intl';
import { useSession } from '~/techstyle-shared/redux-core';

import { trackProductAdded } from '../../actions';

import errorMessages from './errorMessages';
import {
  useProductDetailCTAContext,
  ProductDetailCTAErrorTypes,
} from './ProductDetailCTAContext';
import VIPOptInMessage from './VIPOptInMessage';

const SpeedySignupModal = dynamic(() =>
  import(/* webpackChunkName: "SpeedySignupModal" */ '../SpeedySignupModal')
);

const Wrapper = styled.div``;

const PromoMessage = styled(BasePromoMessage)`
  word-break: break-all;
`;

const promoPriceStyle = css`
  color: ${({ theme }) => theme.colors.textDefault};
  font-size: inherit;
`;

const PreorderRadioWrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.ctaWrapper};
  background: ${({ theme }) => theme.colors.backgroundLight};

  ${mobile`
    padding: 0px 4px;
  `};
`;

const PreorderButtonWrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.ctaWrapper};
  background: ${({ theme }) => theme.colors.backgroundLight};
  border: none;
  padding: 0;
`;

const VIPAddToBagButton = styled(Button)`
  margin: 12px 0;
  color: ${({ theme }) => theme.colors.textLight};
  width: 100%;
`;

const labelStyle = css`
  font-size: 18px;
  font-weight: bold;
  letter-spacing: ${-0.4 / 18}em;
`;

const PreorderRadioButtonGroup = styled(RadioButtonGroup).attrs({
  name: 'confirm-address',
  verticalAlign: 'center',
})`
  grid-gap: 0;

  > ${RadioButton.Wrapper} {
    padding: 16px 0;
  }
`;
const inputStyle = css`
  background: ${({ theme }) => theme.colors.palette.white};
  border-width: 0;
  box-shadow: 0 0 0 7px ${({ theme }) => theme.colors.palette.white},
    0 0 0 8px ${({ theme }) => theme.colors.inputShadow};
  transform: scale(0.75);
  will-change: box-shadow;
  transition: background 0.16s ease-in-out, box-shadow 0.16s ease-in-out;
  svg {
    visibility: visible;
    fill: white;
  }
`;

const inputCheckedStyle = css`
  background: ${({ theme }) => theme.colors.palette.black};
  border-width: 0;
  box-shadow: 0 0 0 0 ${({ theme }) => theme.colors.palette.white},
    0 0 0 8px ${({ theme }) => theme.colors.palette.black};
  outline: none;
`;

const inputFocusStyle = css`
  background: ${({ theme }) => theme.colors.palette.black};
  box-shadow: 0 0 0 0 ${({ theme }) => theme.colors.palette.white},
    0 0 0 8px ${({ theme }) => theme.colors.palette.black};
  filter: brightness(1.5);
`;

const PreorderRadioButton = styled(RadioButton).attrs({
  inputStyle,
  inputCheckedStyle,
  inputFocusStyle,
})`
  cursor: pointer;
  margin-left: -2px;
  outline: none;
`;

const PreorderRadioButtonContent = styled.label`
  cursor: pointer;
`;

const PreorderRadioButtonBlock = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  justify-content: start;
  align-items: baseline;
  padding: 10px 20px;
  ${({ $selected, theme: { colors } }) =>
    $selected &&
    css`
      background: ${colors.preorderRadioButtonBlockSelectedBg};
      border: 1px solid ${colors.preorderRadioBorder};
      border-radius: 8px;
    `}

  ${PreorderRadioButton} {
    flex: 0 0 auto;
  }
  ${PreorderRadioButtonContent} {
    flex: 1 1 auto;
    margin-left: 12px;
  }
`;

const PreorderTitle = styled(Heading).attrs({
  level: 3,
})`
  font-size: 16px;
  font-weight: 300;
  text-transform: capitalize;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.textDefault};
`;

const ShippingInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px 0 10px 0;
  font-size: 14px;
  letter-spacing: ${-0.2 / 14}em;
  color: ${({ theme }) => theme.colors.preorderCtaShippingInfo};
`;

const ShippingDateText = styled.span`
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

const ShippingText = styled.span`
  font-weight: 450;
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: center;
  color: ${({ theme: { colors } }) => colors.sublabelBlack};
`;

const ShippingInfo = () => {
  const { selectionStateByProductIndex } = useProductDetailContext();
  const shippingDate =
    Math.max(
      ...selectionStateByProductIndex.map(
        (selection) => new Date(selection?.selectedSKU?.dateAvailablePreorder)
      )
    ) || null;

  return (
    shippingDate && (
      <ShippingInfoWrapper>
        <ShippingDateText data-autotag="Pre-Order_Estimated_Date">
          <FormattedMessage
            id="site_pdp.pre_order_shipping"
            defaultMessage="Est. Shipping Date:"
          />
          &nbsp;
          <FormattedDate
            value={shippingDate}
            month="2-digit"
            day="2-digit"
            year="2-digit"
          />
        </ShippingDateText>
        <ShippingText>
          <FormattedMessage
            id="site_pdp.pre_order_charge"
            defaultMessage="Your credit card will not be charged until the item has shipped."
          />
        </ShippingText>
      </ShippingInfoWrapper>
    )
  );
};

const guestPreorder = 'guestpreorder-option-input';
const vipPreorder = 'vippreorder-option-input';
const vipMemberCreditPreorder = 'vipmembercreditpreorder-option-input';

const PRE_ORDER_INPUT_TYPE = {
  GUEST: 'inputGuestPreorder',
  VIP: 'inputVipPreorder',
  MEMBER_CREDIT: 'inputVipMemberCreditPreorder',
};

function OptionMessage({ option, product, showPrice = true }) {
  const promoLabel = product.promos?.promoLabel;
  const messageHasNoNumbers = /^([^0-9]*)$/.test(promoLabel);
  const isPercentOffPromo = promoLabel?.includes('%');

  switch (option) {
    case PRE_ORDER_INPUT_TYPE.GUEST:
      return (
        <>
          <FormattedMessage
            id="site_pdp.pre_order_guest_cta"
            defaultMessage="Guest Pre-Order"
          />
          &nbsp;
          {showPrice && (
            <b>
              <Currency amount={product.retailUnitPrice} />
            </b>
          )}
        </>
      );
    case PRE_ORDER_INPUT_TYPE.VIP:
      return (
        <>
          <FormattedMessage
            id="site_pdp.pre_order_vip_cta"
            defaultMessage="VIP Pre-Order"
          />
          &nbsp;
          {showPrice && (
            <b>
              {promoLabel !== '' ? (
                <PromoMessage
                  showPromoPrice={isPercentOffPromo || messageHasNoNumbers}
                  promoPriceStyle={promoPriceStyle}
                />
              ) : (
                <Currency amount={product.defaultUnitPrice} />
              )}
            </b>
          )}
        </>
      );

    case PRE_ORDER_INPUT_TYPE.MEMBER_CREDIT:
      return (
        <>
          <FormattedMessage
            id="site_pdp.pre_order_vip_cta"
            defaultMessage="VIP Pre-Order"
          />
          &nbsp;
          {showPrice && (
            <b>
              <FormattedMessage
                id="site_pdp.pre_order_vip_member_credit_cta"
                defaultMessage="1 Member Credit"
              />
            </b>
          )}
        </>
      );
    default:
      return (
        <>
          <FormattedMessage
            id="site_pdp.pre_order_guest_cta"
            defaultMessage="Guest Pre-Order"
          />
          &nbsp;
          {showPrice && (
            <b>
              <Currency amount={product.retailUnitPrice} />
            </b>
          )}
        </>
      );
  }
}

OptionMessage.propTypes = {
  option: PropTypes.string,
  product: PropTypes.object,
  showPrice: PropTypes.bool,
};

function LoggedOutOptions({ product, selected }) {
  const { isVIPAddToBagEnabled } = useProductDetailCTAContext();
  return (
    <>
      <PreorderRadioButtonBlock
        $selected={selected === PRE_ORDER_INPUT_TYPE.GUEST}
      >
        <PreorderRadioButton
          id={guestPreorder}
          value="inputGuestPreorder"
          disabled={isVIPAddToBagEnabled}
          data-autotag="Pre-Order_Radion_Button_Guest"
        />
        <PreorderRadioButtonContent htmlFor={guestPreorder}>
          <PreorderTitle>
            <OptionMessage option="inputGuestPreorder" product={product} />
          </PreorderTitle>
        </PreorderRadioButtonContent>
      </PreorderRadioButtonBlock>
      <PreorderRadioButtonBlock
        $selected={selected === PRE_ORDER_INPUT_TYPE.VIP}
      >
        <PreorderRadioButton
          id={vipPreorder}
          value="inputVipPreorder"
          data-autotag="Pre-Order_Radion_Button_VIP"
        />
        <PreorderRadioButtonContent htmlFor={vipPreorder}>
          <PreorderTitle>
            <OptionMessage option="inputVipPreorder" product={product} />
          </PreorderTitle>
        </PreorderRadioButtonContent>
      </PreorderRadioButtonBlock>
    </>
  );
}

LoggedOutOptions.propTypes = {
  product: PropTypes.object,
  selected: PropTypes.string,
};

function GuestOptions({ product, selected }) {
  const { isVIPAddToBagEnabled } = useProductDetailCTAContext();
  return (
    <>
      <PreorderRadioButtonBlock
        $selected={selected === PRE_ORDER_INPUT_TYPE.GUEST}
      >
        <PreorderRadioButton
          id={guestPreorder}
          value="inputGuestPreorder"
          disabled={isVIPAddToBagEnabled}
          data-autotag="Pre-Order_Radion_Button_Guest"
        />
        <PreorderRadioButtonContent htmlFor={guestPreorder}>
          <PreorderTitle>
            <OptionMessage option="inputGuestPreorder" product={product} />
          </PreorderTitle>
        </PreorderRadioButtonContent>
      </PreorderRadioButtonBlock>
      <PreorderRadioButtonBlock
        $selected={selected === PRE_ORDER_INPUT_TYPE.VIP}
      >
        <PreorderRadioButton
          id={vipPreorder}
          value="inputVipPreorder"
          data-autotag="Pre-Order_Radion_Button_VIP"
        />
        <PreorderRadioButtonContent htmlFor={vipPreorder}>
          <PreorderTitle>
            <OptionMessage option="inputVipPreorder" product={product} />
          </PreorderTitle>
        </PreorderRadioButtonContent>
      </PreorderRadioButtonBlock>
    </>
  );
}

GuestOptions.propTypes = {
  product: PropTypes.object,
  selected: PropTypes.string,
};

/**
 * Preorder is not available for non-EMP members using membership credits. `VipOptions` uses the terminology
 * 'membership credits' but it should be noted these are 'membership tokens' for EMP members. Once Preorder
 * is available for non-EMP members `VipOptions` will need to be updated to utilize membership tokens as well.
 */
function VipOptions({ product, selected }) {
  const { membershipCredits } = useMembershipCredits({ page: 1 });
  return (
    <>
      <PreorderRadioButtonBlock
        $selected={selected === PRE_ORDER_INPUT_TYPE.MEMBER_CREDIT}
      >
        <PreorderRadioButton
          id={vipMemberCreditPreorder}
          value="inputVipMemberCreditPreorder"
          disabled={membershipCredits.idsByPage['1']?.length === 0}
          data-autotag="Pre-Order_Radion_Button_Guest"
        />
        <PreorderRadioButtonContent htmlFor={vipMemberCreditPreorder}>
          <PreorderTitle>
            <OptionMessage
              option="inputVipMemberCreditPreorder"
              product={product}
            />
          </PreorderTitle>
        </PreorderRadioButtonContent>
      </PreorderRadioButtonBlock>
      <PreorderRadioButtonBlock
        $selected={selected === PRE_ORDER_INPUT_TYPE.VIP}
      >
        <PreorderRadioButton
          id={vipPreorder}
          value="inputVipPreorder"
          data-autotag="Pre-Order_Radion_Button_VIP"
        />
        <PreorderRadioButtonContent htmlFor={vipPreorder}>
          <PreorderTitle>
            <OptionMessage option="inputVipPreorder" product={product} />
          </PreorderTitle>
        </PreorderRadioButtonContent>
      </PreorderRadioButtonBlock>
    </>
  );
}

VipOptions.propTypes = {
  product: PropTypes.object,
  selected: PropTypes.string,
};

function Cta({
  radioState,
  isLoading,
  labelStyle,
  handleCTAClick,
  disabled,
  product,
}) {
  return (
    <VIPAddToBagButton
      variant="primaryAddToBag"
      labelStyle={labelStyle}
      onClick={handleCTAClick}
      data-autotag="pdp-vip-optin-add-to-bag"
      data-cnstrc-btn="add-to-cart"
      disabled={disabled}
      isLoading={isLoading}
    >
      <OptionMessage
        option={radioState.selected}
        product={product}
        showPrice={false}
      />
    </VIPAddToBagButton>
  );
}

Cta.propTypes = {
  disabled: PropTypes.bool,
  handleCTAClick: PropTypes.func,
  isLoading: PropTypes.bool,
  labelStyle: PropTypes.object,
  product: PropTypes.object,
  radioState: PropTypes.object,
};

function RadioOptions({
  isLoggedIn,
  isVip,
  isLead,
  isPaygo,
  product,
  selected,
}) {
  if (!isLoggedIn) {
    return <LoggedOutOptions product={product} selected={selected} />;
  } else if (isLead || isPaygo) {
    return <GuestOptions product={product} selected={selected} />;
  } else if (isVip) {
    return <VipOptions product={product} selected={selected} />;
  }
  return <LoggedOutOptions product={product} selected={selected} />;
}

RadioOptions.propTypes = {
  isLead: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isPaygo: PropTypes.bool,
  isVip: PropTypes.bool,
  product: PropTypes.object,
  selected: PropTypes.string,
};

function PreorderMultiOptionCTA({ product: productFromProps, ...rest }) {
  const intl = useIntl();
  const { isLoggedIn } = useSession();
  const { isLead, isPaygo, isVip } = useMembership();
  const {
    addToCart,
    handlePreorderValidation,
    preorderError,
    isLoading,
    signupAsVIPCart,
    isVIPAddToBagEnabled,
    setErrorMessage,
  } = useProductDetailCTAContext();

  const disabled = isLoading;
  const dispatch = useDispatch();
  const productDetailContext = useProductDetailContext();

  const [isSpeedySignupOpen, setIsSpeedySignupOpen] = useState(false);

  const hideRadioOptions = !isVip && isVIPAddToBagEnabled;

  useEffect(() => {
    if (!preorderError) {
      setErrorMessage(null);
    }
  }, [preorderError, setErrorMessage]);

  const [radioState, setRadioState] = useState({
    selected:
      isVip || isVIPAddToBagEnabled
        ? PRE_ORDER_INPUT_TYPE.VIP
        : PRE_ORDER_INPUT_TYPE.GUEST,
    inputGuestPreorder: isVIPAddToBagEnabled
      ? PRE_ORDER_INPUT_TYPE.VIP
      : PRE_ORDER_INPUT_TYPE.GUEST,
    inputVipPreorder: PRE_ORDER_INPUT_TYPE.VIP,
    inputVipMemberCreditPreorder: PRE_ORDER_INPUT_TYPE.MEMBER_CREDIT,
  });

  const closeSpeedySignup = useCallback(() => {
    setIsSpeedySignupOpen(false);
  }, [setIsSpeedySignupOpen]);
  const openSpeedySignup = useCallback(() => {
    setIsSpeedySignupOpen(true);
  }, [setIsSpeedySignupOpen]);

  const handleCTAClick = useCallback(async () => {
    try {
      switch (radioState.selected) {
        case PRE_ORDER_INPUT_TYPE.GUEST:
          if (!isLoggedIn) {
            await handlePreorderValidation();
            openSpeedySignup();
            return;
          }
          if (isLead || isPaygo) {
            await handlePreorderValidation();
            await addToCart({ enableVIPCart: false });
            dispatch(trackProductAdded({ ...productDetailContext }));
          }
          break;
        case PRE_ORDER_INPUT_TYPE.VIP:
          if (!isLoggedIn) {
            await signupAsVIPCart();
            await handlePreorderValidation();
            openSpeedySignup();
            dispatch(trackProductAdded({ ...productDetailContext }));
            return;
          }
          if (isLead || isPaygo) {
            await signupAsVIPCart();
            await handlePreorderValidation();
            await addToCart({ enableVIPCart: true });
            dispatch(trackProductAdded({ ...productDetailContext }));
            return;
          }
          if (isVip) {
            await handlePreorderValidation();
            await addToCart({ enableVIPCart: true });
            dispatch(trackProductAdded({ ...productDetailContext }));
          }
          break;
        case PRE_ORDER_INPUT_TYPE.MEMBER_CREDIT:
          await handlePreorderValidation();
          await addToCart({ applyMemberCredit: true });
          dispatch(trackProductAdded({ ...productDetailContext }));
          break;
        default:
          break;
      }
    } catch (error) {
      if (error.message === ProductDetailCTAErrorTypes.SelectorError) {
        setErrorMessage(intl.formatMessage(errorMessages.sizeSelectorError));
      }
    }
  }, [
    radioState.selected,
    isLoggedIn,
    isLead,
    isPaygo,
    isVip,
    handlePreorderValidation,
    addToCart,
    dispatch,
    productDetailContext,
    openSpeedySignup,
    signupAsVIPCart,
    setErrorMessage,
    intl,
  ]);

  return (
    <>
      <SpeedySignupModal
        isOpen={isSpeedySignupOpen}
        onExit={closeSpeedySignup}
      />
      <Wrapper {...rest}>
        {!hideRadioOptions && (
          <PreorderRadioWrapper>
            <PreorderRadioButtonGroup
              value={radioState[radioState.selected]}
              onChange={(event) =>
                setRadioState({
                  ...radioState,
                  selected: event.target.value,
                })
              }
            >
              <RadioOptions
                isLoggedIn={isLoggedIn}
                isVip={isVip}
                isLead={isLead}
                isPaygo={isPaygo}
                product={productDetailContext.product}
                selected={radioState.selected}
              />
            </PreorderRadioButtonGroup>
          </PreorderRadioWrapper>
        )}

        {!isVip && <VIPOptInMessage />}

        <ShippingInfo />

        <PreorderButtonWrapper>
          <Cta
            radioState={radioState}
            labelStyle={labelStyle}
            handleCTAClick={handleCTAClick}
            disabled={disabled}
            isLoading={isLoading}
            product={productDetailContext.product}
          />
        </PreorderButtonWrapper>
      </Wrapper>
    </>
  );
}

PreorderMultiOptionCTA.propTypes = {
  product: PropTypes.object,
};

export default PreorderMultiOptionCTA;
